import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, XIcon } from 'lucide-react';
declare global {
  interface Window {
    _wq: any[];
  }
}
function WistiaPlayer({
  isFloating,
  onDismiss
}: {
  isFloating: boolean;
  onDismiss: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Load Wistia player script
    const playerScript = document.createElement('script');
    playerScript.src = 'https://fast.wistia.com/player.js';
    playerScript.async = true;
    document.head.appendChild(playerScript);
    // Load Wistia embed script
    const embedScript = document.createElement('script');
    embedScript.src = 'https://fast.wistia.com/embed/19bxuvo9zk.js';
    embedScript.async = true;
    embedScript.type = 'module';
    document.head.appendChild(embedScript);
    // Create the wistia-player element
    if (containerRef.current) {
      const player = document.createElement('wistia-player');
      player.setAttribute('media-id', '19bxuvo9zk');
      player.setAttribute('aspect', '1.7777777777777777');
      player.style.width = '100%';
      player.style.display = 'block';
      containerRef.current.appendChild(player);
    }
    return () => {
      if (document.head.contains(playerScript)) document.head.removeChild(playerScript);
      if (document.head.contains(embedScript)) document.head.removeChild(embedScript);
    };
  }, []);
  return <div ref={containerRef} className={`relative z-10 w-full rounded-lg shadow-2xl overflow-hidden transition-all duration-500 ease-in-out ${isFloating ? 'fixed bottom-4 right-4 !w-[300px] md:!w-[380px] z-[90] rounded-xl border border-gold/30' : ''}`} style={isFloating ? {
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)'
  } : {}} data-id="element-232">
      <AnimatePresence data-id="element-233">
        {isFloating && <motion.button initial={{
        opacity: 0,
        scale: 0.5
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.5
      }} transition={{
        duration: 0.2
      }} onClick={onDismiss} className="absolute top-2 right-2 z-20 p-1.5 rounded-full bg-black/70 hover:bg-black/90 border border-white/20 transition-colors" aria-label="Cerrar video flotante" data-id="element-234">
            <XIcon className="h-3.5 w-3.5 text-white" data-id="element-235" />
          </motion.button>}
      </AnimatePresence>
    </div>;
}
export function HeroV2({
  onVideoMilestone,
  showCTA = true
}: {
  onVideoMilestone?: () => void;
  showCTA?: boolean;
}) {
  const [currentPrice] = useState(27);
  const [isFloating, setIsFloating] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const videoSentinelRef = useRef<HTMLDivElement>(null);
  const [countdown, setCountdown] = useState(165); // 2 minutes 45 seconds
  const countdownFinishedRef = useRef(false);
  // Countdown timer
  useEffect(() => {
    if (countdownFinishedRef.current) return;
    if (countdown <= 0) {
      countdownFinishedRef.current = true;
      onVideoMilestone?.();
      return;
    }
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown, onVideoMilestone]);
  // Floating video observer
  useEffect(() => {
    const sentinel = videoSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) {
        setIsFloating(true);
      } else {
        setIsFloating(false);
        setIsDismissed(false);
      }
    }, {
      threshold: 0.1
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);
  const handleDismiss = () => {
    setIsDismissed(true);
  };
  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;
  const timerDone = countdown <= 0;
  return <section className="relative w-full flex items-center justify-center overflow-hidden px-4 py-16 md:py-20" data-id="element-236">
      {/* Animated background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy-light to-navy-dark" data-id="element-237">
        <motion.div animate={{
        scale: [1, 1.2, 1],
        opacity: [0.3, 0.5, 0.3]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute top-0 right-0 w-96 h-96 bg-gold/20 rounded-full blur-3xl" data-id="element-238" />
        <motion.div animate={{
        scale: [1.2, 1, 1.2],
        opacity: [0.2, 0.4, 0.2]
      }} transition={{
        duration: 10,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute bottom-0 left-0 w-96 h-96 bg-gold/10 rounded-full blur-3xl" data-id="element-239" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto w-full" data-id="element-240">
        {/* Headline */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="text-center mb-6 md:mb-8" data-id="element-241">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="font-heading font-black text-4xl md:text-5xl lg:text-6xl leading-tight mb-4 md:mb-6 text-white" data-id="element-242">
            <span className="text-gold" data-id="element-243">$1,050.</span> Primera semana. Cero
            experiencia en diseño.
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} className="font-body text-lg md:text-xl text-gray-300 mb-2 max-w-3xl mx-auto" data-id="element-244">
            Así fue como un miembro usó nuestra IA para construir sitios web en
            minutos y venderlos a negocios locales.
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.7
        }} className="font-body text-lg md:text-xl text-gray-400 mb-2 max-w-3xl mx-auto" data-id="element-245">
            Sin ads. Sin saber programar. Solo llamadas y cobros.
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.8
        }} className="font-heading font-bold text-xl md:text-2xl text-gold mb-4 max-w-3xl mx-auto" data-id="element-246">
            Tú puedes hacer lo mismo hoy
          </motion.p>
        </motion.div>

        {/* Hero Video */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} className="relative mb-4" data-id="element-247">
          <div ref={videoSentinelRef} className="absolute inset-0 pointer-events-none" data-id="element-248" />
          <WistiaPlayer isFloating={!isDismissed && isFloating} onDismiss={handleDismiss} data-id="element-249" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-transparent rounded-lg pointer-events-none" data-id="element-250" />
        </motion.div>

        {/* Countdown Timer */}
        <AnimatePresence data-id="element-251">
          {!timerDone && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.5
        }} className="flex flex-col items-center justify-center gap-2 mb-8" data-id="element-252">
              <p className="font-body text-sm text-gray-400" data-id="element-253">
                El contenido se desbloquea en:
              </p>
              <div className="flex items-center gap-1.5 md:gap-2" data-id="element-254">
                <div className="bg-navy text-gold font-heading font-black text-lg md:text-xl px-3 py-2 rounded border border-gold/30 min-w-[48px] md:min-w-[56px] text-center" data-id="element-255">
                  {minutes.toString().padStart(2, '0')}
                </div>
                <span className="text-gold font-black text-lg" data-id="element-256">:</span>
                <div className="bg-navy text-gold font-heading font-black text-lg md:text-xl px-3 py-2 rounded border border-gold/30 min-w-[48px] md:min-w-[56px] text-center" data-id="element-257">
                  {seconds.toString().padStart(2, '0')}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* CTA Below Video - delayed until countdown finishes */}
        <AnimatePresence data-id="element-258">
          {showCTA && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-259">
              <motion.a href="/checkout" onClick={() => {
            if (window.fbq) window.fbq('track', 'AddToCart', {
              content_name: 'AI Web Closer Course',
              content_type: 'product',
              value: 27,
              currency: 'USD'
            });
          }} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="group bg-gold hover:bg-gold-light text-navy font-heading font-bold text-xl px-10 py-5 rounded-xl shadow-2xl transition-all duration-300 flex items-center justify-center gap-3" data-id="element-260">
                Quiero Acceso por ${currentPrice}
                <ArrowRightIcon className="h-6 w-6 group-hover:translate-x-1 transition-transform" aria-hidden="true" data-id="element-261" />
              </motion.a>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}